import React, { useState } from 'react';
import { PhotoIssueTypeInput } from 'data/__generated__';
import styled from 'styled-components';
import { TotemPrimaryButton } from './TotemPrimaryButton';
import { TotemCheckbox } from './TotemCheckbox';
import { TotemPopup } from './TotemPopup';

interface PhotoIssuePopupProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (issueType: PhotoIssueTypeInput, comment: string) => void;
}

export const PhotoIssuePopup: React.FC<PhotoIssuePopupProps> = ({ isOpen, onClose, onConfirm }) => {
    const [issueType, setIssueType] = useState<PhotoIssueTypeInput>({
        isBadQuality: false,
        isBadQuantity: false,
    });
    const [comment, setComment] = useState('');

    const hasIssue = issueType.isBadQuality || issueType.isBadQuantity;
    const isValid = hasIssue && comment.trim().length > 0;

    const resetAndClose = () => {
        setIssueType({ isBadQuality: false, isBadQuantity: false });
        setComment('');
        onClose();
    };

    const handleConfirm = () => {
        if (isValid) {
            onConfirm(issueType, comment.trim());
            resetAndClose();
        }
    };

    const toggleIssue = (issue: keyof PhotoIssueTypeInput) => {
        setIssueType((prev) => ({
            ...prev,
            [issue]: !prev[issue],
        }));
    };

    if (!isOpen) return null;

    return (
        <TotemPopup title="Sélectionner les problèmes" isOpen={isOpen} setIsOpen={resetAndClose}>
            <CheckboxContainer>
                <TotemCheckbox
                    label="Mauvaise qualité"
                    checked={issueType.isBadQuality}
                    onChange={() => toggleIssue('isBadQuality')}
                />
                <TotemCheckbox
                    label="Mauvaise quantité"
                    checked={issueType.isBadQuantity}
                    onChange={() => toggleIssue('isBadQuantity')}
                />
            </CheckboxContainer>
            <CommentLabel>Commentaire</CommentLabel>
            <CommentTextarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Décrivez le problème..."
                rows={3}
            />
            <ButtonContainer>
                <TotemPrimaryButton $isSecondaryStyle onClick={resetAndClose}>
                    Annuler
                </TotemPrimaryButton>
                <TotemPrimaryButton onClick={handleConfirm} disabled={!isValid}>
                    Confirmer
                </TotemPrimaryButton>
            </ButtonContainer>
        </TotemPopup>
    );
};

const CheckboxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
`;

const CommentLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
`;

const CommentTextarea = styled.textarea`
    padding: 12px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    font-size: 14px;
    resize: vertical;
    min-height: 80px;
    margin-bottom: 20px;

    &::placeholder {
        color: ${({ theme }) => theme.infoTextColor};
    }

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;
`;
